import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DIALOG_TYPES, DialogScheduleModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CoreModule } from '../../../../core/core.module';
import { ScheduleUtil } from '../../../../helpers/schedule/schedule-util';
import { ScheduleService } from '../../../../services/schedule.service';
import { SharedModule } from '../../../shared/shared.module';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-schedules-dialog',
  imports: [CommonModule, SharedModule, ReactiveFormsModule, CoreModule],
  templateUrl: './schedules-dialog.component.html',
  styleUrl: './schedules-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SchedulesDialogComponent implements OnInit {
  protected readonly DIALOG_TYPES = DIALOG_TYPES;
  public isScheduleConfirmedDelete: WritableSignal<boolean> = signal<boolean>(false);
  public deleteFormControl: FormControl = new FormControl<string | null>(null);
  public subGroups: WritableSignal<any[]> = signal<any[]>([]);
  showSpinner: WritableSignal<boolean> = signal<boolean>(false);
  public scheduleUtil: typeof ScheduleUtil = ScheduleUtil;
  scheduleForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    lineName: new FormControl('', [Validators.required]),
    orientation: new FormControl('Collapse')
  });

  public isCheckedAll = computed(() => {
    return this.subGroups().filter(group => group.isChecked).length === this.subGroups().length;
  });

  public isCheckedOne = computed(() => {
    return this.subGroups().find(group => group.isChecked) !== undefined;
  });

  constructor(public dialogRef: MatDialogRef<SchedulesDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: DialogScheduleModel, private scheduleService: ScheduleService,
              public toasterService: ToastrService) {

  }

  ngOnInit() {
    if (this.data.type === this.DIALOG_TYPES.MANUAL || this.data.type === this.DIALOG_TYPES.GROUP) {
      this.scheduleForm.patchValue({
        name: this.data.selectedSchedule?.name || ''
      });
      this.scheduleForm.get('name').disable();
      this.scheduleForm.get('name').updateValueAndValidity();
      if (this.data.type === this.DIALOG_TYPES.GROUP) {
        this.subGroups.set(this.data.selectedSchedule?.subGroups || []);
      }
    }
  }

  public deleteSchedule() {
    const selectedSchedule: any = this.data.selectedSchedule;
    console.log('selectedSchedule', selectedSchedule);
    const uuid = selectedSchedule?.uuid || selectedSchedule?.combined_id;
    const scheduleName = (selectedSchedule?.schedule_name ? selectedSchedule?.schedule_name: ('Schedule '+ selectedSchedule?.sortOrder));
    const groupName = scheduleName +' - '+ (selectedSchedule?.nameChange || selectedSchedule?.groupName || selectedSchedule?.name);
    this.scheduleService.updateSpinner(true);
    this.showSpinner.set(true);
    this.scheduleService.deleteSchedule(uuid).pipe(finalize(() => {this.scheduleService.updateSpinner(false);  this.showSpinner.set(false);})).subscribe({
      next: (res) => {
        if (res?.status === 200) {
          if (this.data.type === this.DIALOG_TYPES.RESET) {
            this.dialogRef.close({success: true});
          } else {
            this.toasterService.success(`${groupName} removed successfully under Schedules section.`);
            this.dialogRef.close({success: true});
          }
        } else {
          this.dialogRef.close({success: false, message: res?.messages});
        }
      },
      error: (err) => {
        this.dialogRef.close({success: false, message: err.message});
      }
    });
  }

  public confirmDelete() {
    if (this.isScheduleConfirmedDelete() || (this.data.type === this.DIALOG_TYPES.RESET)) {
      this.deleteSchedule();
    } else {
      this.isScheduleConfirmedDelete.set(true);
      this.data.headerText = 'Delete Schedule';
      this.data.subHeaderText = null;
      this.data.errorText = 'Note: This is an irreversible action.';
      this.data.subInfoText = 'If you still wish to proceed, please enter `Delete` below';
      this.data.buttonConfirmText = 'Confirm';
    }
  }

  public createScheduleManual() {
    const scheduleForm = {
      groupName: this.scheduleForm.get('lineName').value,
      orientation: this.scheduleForm.get('orientation').value === 'Collapse'
    }
    const filterSchedule = this.subGroups().length ? this.subGroups().filter(group => group.isChecked) : [];
    const scheduleList = this.scheduleUtil.createSchedule({type: this.data.type},scheduleForm,filterSchedule);
    this.scheduleService.addOrResetSchedules(scheduleList, this.data.selectedSchedule?.isPreviousYear, null, this.data.selectedSchedule?.isBSSch);
  }

  public onChange(event: MatCheckboxChange, index: number) {
    this.subGroups.update(groups => {
      return groups.map((group, i) => i === index ? {...group, isChecked: event.checked} : group);
    });
  }

  public onAllChange(event: MatCheckboxChange) {
    this.subGroups.update(groups => {
      return groups.map(group => ({...group, isChecked: event.checked}));
    });
  }

}
